import { UiProgressButton } from '@experiences/ui-common';
import { validateUsername } from '@experiences/util';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    MaxFirstNameLength,
    MaxLastNameLength,
    MaxUsernameLength,
} from '../../../common/constants/UsersConstant';
import validateEmail from '../../../util/validators/EmailValidator';
import EditPasswordFormComponent from '../../common/EditPasswordFormComponent';
import { UiDrawer } from '../../common/UiDrawer';
import UiForm from '../../common/UiForm';
import EditGroupMembershipFormComponent from './EditGroupMembershipFormComponent';
import EditUserAuthSettingsFormComponent from './EditUserAuthSettingsFormComponent';
import useAddEditUserViewModel from './useAddEditUserViewModel';

const useStyles = makeStyles(theme =>
    createStyles({
        input: { marginTop: '20px' },
        inputLabel: {
            fontWeight: 600,
            fontSize: '14px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        inputMargin: { marginBottom: '12px' },
        cancelButton: { marginRight: '10px' },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
    }),
);

const AddEditUserComponent: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();

    const {
        type,
        loading,
        isHostMode,
        onSubmit,
        methods,
        watchBypassPasswordRestriction,
        groups,
        open,
        closeModal,
        showDrawerError,
        errorMessage,
    } = useAddEditUserViewModel();

    const {
        handleSubmit, register, formState: {
            isDirty, isValid, errors,
        },
    } = methods;

    return (
        <UiDrawer
            title={translate({ id: type === 'add' ? 'CLIENT_ADD_USER' : 'CLIENT_EDIT_USER' })}
            drawerProps={{
                anchor: 'right',
                open,
                onClose: () => closeModal(true),
            }}
            loading={!isHostMode && !groups.length}
            error={{
                showError: showDrawerError,
                message: errorMessage,
            }}
        >
            <UiForm
                onSubmit={handleSubmit(onSubmit)}
                actions={
                    <div className={classes.actions}>
                        <Button
                            className={classes.cancelButton}
                            onClick={() => closeModal(true)}
                            color="primary"
                            data-cy='add-edit-cancel-button'>
                            {translate({ id: 'CLIENT_CANCEL' })}
                        </Button>
                        <UiProgressButton
                            type="submit"
                            loading={loading}
                            disabled={!isDirty || !isValid}
                            variant="contained"
                            data-cy="add-edit-submit-button"
                        >
                            {translate({ id: 'CLIENT_SAVE' })}
                        </UiProgressButton>
                    </div>
                }
                isDrawer
            >
                <div className={classes.input}>
                    <TextField
                        label={translate({ id: 'CLIENT_USERNAME' })}
                        disabled={type === 'edit'}
                        required
                        error={!!errors.userName}
                        helperText={
                            (errors.userName?.type === 'required'
                                && translate(
                                    { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                    { 0: translate({ id: 'CLIENT_USERNAME' }) },
                                ))
                            || (errors.userName?.type === 'maxLength'
                                && translate(
                                    { id: 'CLIENT_USERNAME_MAX_LENGTH_ERROR' },
                                    { 0: MaxUsernameLength },
                                ))
                            || (errors.userName?.type === 'available'
                                && translate({ id: 'CLIENT_USERNAME_TAKEN_ERROR' }))
                            || (errors.userName?.type === 'valid'
                                && translate({ id: 'CLIENT_INVALID_USERNAME_ERROR' }))
                        }
                        name="userName"
                        variant="outlined"
                        autoComplete="off"
                        fullWidth
                        InputProps={{ className: 'Tall' }}
                        inputProps={{
                            'aria-label': translate({ id: 'CLIENT_USERNAME' }),
                            ...register('userName', {
                                required: true,
                                maxLength: MaxUsernameLength,
                                validate: { valid: value => validateUsername(value) },
                            }),
                        }}
                        data-cy="add-edit-username"
                    />
                </div>
                <div className={classes.input}>
                    <TextField
                        label={translate({ id: 'CLIENT_FIRST_NAME' })}
                        required
                        error={!!errors.firstName}
                        helperText={
                            (errors.firstName?.type === 'required'
                                && translate(
                                    { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                    { 0: translate({ id: 'CLIENT_FIRST_NAME' }) },
                                ))
                            || (errors.firstName?.type === 'maxLength'
                                && translate(
                                    { id: 'CLIENT_FIRST_NAME_MAX_LENGTH_ERROR' },
                                    { 0: MaxFirstNameLength },
                                ))
                        }
                        name="firstName"
                        variant="outlined"
                        autoComplete="off"
                        fullWidth
                        InputProps={{ className: 'Tall' }}
                        inputProps={{
                            'aria-label': translate({ id: 'CLIENT_FIRST_NAME' }),
                            ...register('firstName', {
                                required: true,
                                maxLength: MaxFirstNameLength,
                            }),
                        }}
                        data-cy="add-edit-first-name"
                    />
                </div>
                <div className={classes.input}>
                    <TextField
                        label={translate({ id: 'CLIENT_LAST_NAME' })}
                        required
                        error={!!errors.lastName}
                        helperText={
                            (errors.lastName?.type === 'required'
                                && translate(
                                    { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                    { 0: translate({ id: 'CLIENT_LAST_NAME' }) },
                                ))
                            || (errors.lastName?.type === 'maxLength'
                                && translate(
                                    { id: 'CLIENT_LAST_NAME_MAX_LENGTH_ERROR' },
                                    { 0: MaxLastNameLength },
                                ))
                        }
                        variant="outlined"
                        autoComplete="off"
                        fullWidth
                        InputProps={{ className: 'Tall' }}
                        inputProps={{
                            'aria-label': translate({ id: 'CLIENT_LAST_NAME' }),
                            ...register('lastName', {
                                required: true,
                                maxLength: MaxLastNameLength,
                            }),
                        }}
                        data-cy="add-edit-last-name"
                    />
                </div>
                <div className={classes.input}>
                    <TextField
                        label={translate({ id: 'CLIENT_EMAIL' })}
                        variant="outlined"
                        type="email"
                        error={!!errors.email}
                        helperText={
                            errors.email?.type === 'validate'
                                ? translate({ id: 'CLIENT_INVALID_EMAIL_ERROR' })
                                : errors.email?.type === 'available' && translate({ id: 'CLIENT_EMAIL_TAKEN_ERROR' })
                        }
                        autoComplete="off"
                        fullWidth
                        InputProps={{ className: 'Tall' }}
                        inputProps={{
                            'aria-label': translate({ id: 'CLIENT_EMAIL' }),
                            ...register('email', { validate: value => !value || validateEmail(value) }),
                        }}
                        data-cy="add-edit-email"
                    />
                </div>
                <FormProvider {...methods}>
                    <EditPasswordFormComponent
                        respectPasswordRequirements
                        required={watchBypassPasswordRestriction}
                    />
                    {!isHostMode && <EditUserAuthSettingsFormComponent />}
                    {!isHostMode && <EditGroupMembershipFormComponent groups={groups} />}
                </FormProvider>
            </UiForm>
        </UiDrawer>
    );
};

export default AddEditUserComponent;
